import { memo, useCallback, useEffect, useMemo, useRef, useSyncExternalStore, type ReactNode } from "react";
import { PAGE_GAP, type BoardBackground, type LayerElement, type PageElement, type SepiaElement } from "../../../shared/model";
import { InputController } from "../input";
import { bounds, frameBox } from "../elements";
import { type Box, type Vec, boxesIntersect, center, expandBox, rotate } from "../geometry";
import { deskColor, PAPERS } from "../style";
import { handlePositions } from "../tools/transform";
import { ElementView, InkRun } from "./views";
import { useCamera, useDocVersion, useEditor, useOverlay, useStore, useUi } from "./hooks";

// ---------- paper background ----------

function applyBackground(node: HTMLElement, bg: BoardBackground, x: number, y: number, z: number): void {
  const paper = PAPERS[bg.paper] ?? PAPERS.cream;
  node.style.backgroundColor = paper.color;
  if (bg.pattern === "blank") {
    node.style.backgroundImage = "none";
    return;
  }
  // level of detail: keep grid cells at least ~9 screen px apart
  let cell = bg.size;
  while (cell * z < 9) cell *= 5;
  const c = cell * z;
  const major = c * 5;
  const mx = ((x % major) + major) % major;
  const my = ((y % major) + major) % major;
  const cx = ((x % c) + c) % c;
  const cy = ((y % c) + c) % c;
  const fade = Math.min(1, Math.max(0, (c - 9) / 12));
  const minor = paper.line.replace(/[\d.]+\)$/, (m) => `${(parseFloat(m) * fade).toFixed(3)})`);
  if (bg.pattern === "grid") {
    node.style.backgroundImage = [
      `linear-gradient(to right, ${paper.major} 1px, transparent 1px)`,
      `linear-gradient(to bottom, ${paper.major} 1px, transparent 1px)`,
      `linear-gradient(to right, ${minor} 1px, transparent 1px)`,
      `linear-gradient(to bottom, ${minor} 1px, transparent 1px)`
    ].join(",");
    node.style.backgroundSize = `${major}px ${major}px, ${major}px ${major}px, ${c}px ${c}px, ${c}px ${c}px`;
    node.style.backgroundPosition = `${mx}px ${my}px, ${mx}px ${my}px, ${cx}px ${cy}px, ${cx}px ${cy}px`;
  } else if (bg.pattern === "dots") {
    const r = Math.max(0.9, Math.min(1.8, c / 14));
    node.style.backgroundImage = [
      `radial-gradient(circle, ${paper.major} ${r + 0.4}px, transparent ${r + 1}px)`,
      `radial-gradient(circle, ${minor} ${r}px, transparent ${r + 0.6}px)`
    ].join(",");
    node.style.backgroundSize = `${major}px ${major}px, ${c}px ${c}px`;
    node.style.backgroundPosition = `${mx - major / 2}px ${my - major / 2}px, ${cx - c / 2}px ${cy - c / 2}px`;
  } else if (bg.pattern === "lines") {
    const lc = c * 1.25;
    const ly = ((y % lc) + lc) % lc;
    node.style.backgroundImage = `linear-gradient(to bottom, ${paper.major} 1px, transparent 1px)`;
    node.style.backgroundSize = `${lc}px ${lc}px`;
    node.style.backgroundPosition = `0 ${ly}px`;
  } else {
    // isometric: three families of lines, drawn as a tile
    const w = c * Math.sqrt(3);
    const h = c;
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h * 2}'><path d='M0,0L${w},${h * 2}M0,${h * 2}L${w},0M0,${h}H${w}' stroke='${paper.major}' stroke-width='1' fill='none'/></svg>`;
    const tx = ((x % w) + w) % w;
    const ty = ((y % (h * 2)) + h * 2) % (h * 2);
    node.style.backgroundImage = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
    node.style.backgroundSize = `${w}px ${h * 2}px`;
    node.style.backgroundPosition = `${tx}px ${ty}px`;
  }
}

/** On a paged board the screen behind the pages is a plain desk. */
function applyDesk(node: HTMLElement, bg: BoardBackground): void {
  node.style.backgroundColor = deskColor(bg);
  node.style.backgroundImage = "none";
}

/**
 * Pages draw their own ruling in world units (so it starts at each page's edge). These variables keep the lines
 * one screen pixel thin and thin out a grid that would get too dense; they are refreshed once a zoom settles.
 */
function applyPageVars(world: HTMLElement, bg: BoardBackground, z: number): void {
  const paper = PAPERS[bg.paper] ?? PAPERS.cream;
  let cell = bg.size;
  while (cell * z < 9) cell *= 5;
  const fade = Math.min(1, Math.max(0, (cell * z - 9) / 12));
  const hair = Math.max(0.05, 1 / z);
  const minor = paper.line.replace(/[\d.]+\)$/, (m) => `${(parseFloat(m) * fade).toFixed(3)})`);
  const iso = `<svg xmlns='http://www.w3.org/2000/svg' width='${cell * Math.sqrt(3)}' height='${cell * 2}'><path d='M0,0L${cell * Math.sqrt(3)},${cell * 2}M0,${cell * 2}L${cell * Math.sqrt(3)},0M0,${cell}H${cell * Math.sqrt(3)}' stroke='${paper.major}' stroke-width='${hair}' fill='none'/></svg>`;
  const s = world.style;
  s.setProperty("--page-paper", paper.color);
  s.setProperty("--page-line", minor);
  s.setProperty("--page-major", paper.major);
  s.setProperty("--cell", cell + "px");
  s.setProperty("--rule", bg.size * 1.25 + "px");
  s.setProperty("--hair", hair + "px");
  s.setProperty("--iso-tile", `url("data:image/svg+xml,${encodeURIComponent(iso)}")`);
}

function usePages(): PageElement[] {
  const ed = useEditor();
  const sub = useCallback((fn: () => void) => ed.doc.subscribe((c) => c.structural && fn()), [ed]);
  return useSyncExternalStore(sub, () => ed.doc.pages(), () => ed.doc.pages());
}

/** The union of the pages, as a clip path in world coordinates: ink stops at the page edge, like on paper. */
function pagesClip(pages: PageElement[]): string {
  return pages.map((p) => `M${p.x},${p.y}h${p.w}v${p.h}h${-p.w}Z`).join("");
}

/** Sheets of paper (with their number underneath), and a button to add one more after the last. */
function PageSheets({ pages }: { pages: PageElement[] }) {
  const ed = useEditor();
  const pattern = useUi((_, e) => e.bg.pattern);
  const readOnly = useUi((ui) => ui.readOnly);
  const last = pages[pages.length - 1];
  return (
    <>
      {pages.map((pg, i) => (
        <div key={pg.id} className={"page-sheet ruling-" + (pg.pattern ?? pattern)} style={{ transform: `translate(${pg.x}px, ${pg.y}px)`, width: pg.w, height: pg.h }}>
          <span className="page-number">{i + 1}</span>
        </div>
      ))}
      {!readOnly && last && (
        <button
          type="button"
          className="page-add"
          data-canvas-ignore
          style={{ transform: `translate(${last.x}px, ${last.y + last.h + PAGE_GAP + 26}px)`, width: last.w }}
          onClick={() => {
            const id = ed.addPage();
            if (id) ed.fitPageWidth(id, true);
          }}
        >
          ＋ Add page
        </button>
      )}
    </>
  );
}

// ---------- scene (world layer contents) ----------

type Item = { kind: "run"; key: string; ids: string[] } | { kind: "el"; el: SepiaElement };

const CULL_FROM = 700;

function useCullBox(): Box | null {
  const ed = useEditor();
  const boxRef = useRef<Box | null>(null);
  const sub = useCallback(
    (fn: () => void) =>
      ed.camera.subscribe(() => {
        const view = ed.camera.view();
        const b = boxRef.current;
        if (!b || view.x < b.x || view.y < b.y || view.x + view.w > b.x + b.w || view.y + view.h > b.y + b.h || view.w < b.w * 0.25) fn();
      }),
    [ed]
  );
  const get = () => {
    const view = ed.camera.view();
    const b = boxRef.current;
    if (b && view.x >= b.x && view.y >= b.y && view.x + view.w <= b.x + b.w && view.y + view.h <= b.y + b.h && view.w >= b.w * 0.25) return b;
    boxRef.current = expandBox(view, Math.max(view.w, view.h) * 0.6);
    return boxRef.current;
  };
  return useSyncExternalStore(sub, get, get);
}

function useTransientExtras(): string {
  const ed = useEditor();
  const sub = useCallback((fn: () => void) => ed.subscribeTransient(fn), [ed]);
  const get = () => ed.draftIds().join(",");
  return useSyncExternalStore(sub, get, get);
}

function sameIds(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** One layer on the canvas: its opacity and blend mode apply to everything on it together, as in Photoshop. */
const LayerGroup = memo(function LayerGroup({ layer, children }: { layer: LayerElement; children: ReactNode }) {
  const opacity = layer.opacity ?? 1;
  return (
    <div className="layer-group" data-layer={layer.id} style={opacity !== 1 || layer.blend !== "normal" ? { opacity, mixBlendMode: layer.blend === "normal" ? undefined : layer.blend } : undefined}>
      {children}
    </div>
  );
});

function Scene() {
  const ed = useEditor();
  const structure = useDocVersion(true);
  const cull = useCullBox();
  const extras = useTransientExtras();
  // reuse unchanged runs so their (memoised) SVGs aren't reconciled again after every stroke
  const prevRuns = useRef(new Map<string, string[]>());
  const groups = useMemo(() => {
    const all = ed.doc.all();
    const doCull = all.length > CULL_FROM && cull;
    const layers = ed.layers();
    const byLayer = new Map<string, { layer: LayerElement; items: Item[]; run: { kind: "run"; key: string; ids: string[] } | null }>();
    for (const l of layers) byLayer.set(l.id, { layer: l, items: [], run: null });
    for (const el of all) {
      const g = byLayer.get(ed.layerOf(el));
      if (!g || !g.layer.visible) continue;
      if (doCull && !boxesIntersect(bounds(el), cull!) && !ed.ui.selection.includes(el.id)) continue;
      if (el.type === "ink") {
        if (!g.run || g.run.ids.length >= 400) {
          g.run = { kind: "run", key: "run-" + el.id, ids: [] };
          g.items.push(g.run);
        }
        g.run.ids.push(el.id);
      } else {
        g.run = null;
        g.items.push({ kind: "el", el });
      }
    }
    const next = new Map<string, string[]>();
    for (const g of byLayer.values()) {
      for (const it of g.items) {
        if (it.kind !== "run") continue;
        const prev = prevRuns.current.get(it.key);
        if (prev && sameIds(prev, it.ids)) it.ids = prev;
        next.set(it.key, it.ids);
      }
    }
    prevRuns.current = next;
    return layers.filter((l) => l.visible).map((l) => byLayer.get(l.id)!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ed, structure, cull]);
  const extraEls = extras ? extras.split(",").map((id) => ed.getEl(id)).filter(Boolean) as SepiaElement[] : [];
  return (
    <>
      {groups.map((g) => (
        <LayerGroup key={g.layer.id} layer={g.layer}>
          {g.items.map((it) => (it.kind === "run" ? <InkRun key={it.key} ids={it.ids} /> : <ElementView key={it.el.id} el={it.el} />))}
        </LayerGroup>
      ))}
      {extraEls.map((el) => (
        <ElementView key={"x-" + el.id} el={el} />
      ))}
    </>
  );
}

// ---------- selection frame & handles (screen space) ----------

function useSelectionGeometry() {
  const ed = useEditor();
  const sub = useCallback(
    (fn: () => void) => {
      const offs = [ed.subscribeUi(fn), ed.subscribeTransient(fn), ed.camera.subscribe(fn), ed.doc.subscribe(fn)];
      return () => offs.forEach((o) => o());
    },
    [ed]
  );
  return useStore(sub, () => {
    const sel = ed.selected();
    if (!sel.length) {
      return { frame: null as null | Vec[], handles: [] as { h: string; p: Vec }[], outlines: [] as Vec[][], locked: false, busy: ed.ui.busy, hover: hoverOutline(), editing: false };
    }
    const editing = !!ed.ui.editingId && sel.length === 1 && ed.ui.editingId === sel[0].id;
    const cam = ed.camera;
    const f = ed.selectionFrame()!;
    const c = center(f.box);
    const corners = [
      { x: f.box.x, y: f.box.y },
      { x: f.box.x + f.box.w, y: f.box.y },
      { x: f.box.x + f.box.w, y: f.box.y + f.box.h },
      { x: f.box.x, y: f.box.y + f.box.h }
    ].map((p) => cam.toScreen(rotate(p, f.rot, c)));
    const outlines = sel.length > 1 ? sel.slice(0, 200).map((e) => rectPts(bounds(e)).map((p) => cam.toScreen(p))) : [];
    const lineOnly = sel.length === 1 && sel[0].type === "line";
    return {
      frame: lineOnly ? null : corners,
      handles: ed.ui.busy || editing ? [] : handlePositions(ed),
      outlines,
      locked: sel.every((e) => e.locked),
      busy: ed.ui.busy,
      hover: hoverOutline(),
      editing
    };

    function hoverOutline(): Vec[] | null {
      const id = ed.ui.hoverId;
      if (!id || ed.ui.selection.includes(id)) return null;
      const el = ed.getEl(id);
      if (!el) return null;
      const b = frameBox(el);
      const cc = center(b);
      return rectPts(b).map((p) => ed.camera.toScreen(rotate(p, el.type === "line" ? 0 : el.rot, cc)));
    }
  }, (a, b) => JSON.stringify(a) === JSON.stringify(b));
}

function rectPts(b: Box): Vec[] {
  return [
    { x: b.x, y: b.y },
    { x: b.x + b.w, y: b.y },
    { x: b.x + b.w, y: b.y + b.h },
    { x: b.x, y: b.y + b.h }
  ];
}

const poly = (pts: Vec[]) => pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

function SelectionLayer() {
  const g = useSelectionGeometry();
  return (
    <svg className="selection-layer">
      {g.hover && <polygon points={poly(g.hover)} className="hover-outline" />}
      {g.outlines.map((o, i) => (
        <polygon key={i} points={poly(o)} className="member-outline" />
      ))}
      {g.frame && <polygon points={poly(g.frame)} className={"selection-frame" + (g.locked ? " locked" : "") + (g.editing ? " editing" : "")} />}
      {!g.locked &&
        g.handles.map(({ h, p }) =>
          h === "rot" ? (
            <g key={h} className="handle-rot" transform={`translate(${p.x} ${p.y})`}>
              <circle r="11" />
              <path d="M-4.5,-1.5 A5,5 0 1,1 -1.5,4.6" />
              <path d="M-7,-3.5 L-4.5,-1.5 L-2.2,-4.2" />
            </g>
          ) : (
            <circle key={h} cx={p.x} cy={p.y} r={h === "pm" ? 6 : 7} className={"handle" + (h === "pm" ? " handle-mid" : "")} />
          )
        )}
    </svg>
  );
}

// ---------- tool overlays (screen space) ----------

function OverlayLayer() {
  const ed = useEditor();
  const ov = useOverlay((o) => o);
  const cam = useCamera((c) => ({ x: c.x, y: c.y, z: c.z, w: c.width, h: c.height }));
  const s = (p: Vec) => ({ x: p.x * cam.z + cam.x, y: p.y * cam.z + cam.y });
  const anchorEl = ov.anchorHint ? ed.getEl(ov.anchorHint.id) : null;
  return (
    <svg className="overlay-layer">
      {ov.marquee && (() => {
        const a = s({ x: ov.marquee.x, y: ov.marquee.y });
        return <rect className="marquee" x={a.x} y={a.y} width={ov.marquee.w * cam.z} height={ov.marquee.h * cam.z} />;
      })()}
      {ov.lasso && ov.lasso.length > 1 && <polygon className="lasso" points={poly(ov.lasso.map(s))} />}
      {ov.guides.map((g, i) =>
        g.x !== undefined ? (
          <line key={i} className="guide" x1={s({ x: g.x, y: 0 }).x} x2={s({ x: g.x, y: 0 }).x} y1={0} y2={cam.h} />
        ) : (
          <line key={i} className="guide" y1={s({ x: 0, y: g.y! }).y} y2={s({ x: 0, y: g.y! }).y} x1={0} x2={cam.w} />
        )
      )}
      {anchorEl && (() => {
        const b = frameBox(anchorEl);
        const c = center(b);
        const pts = rectPts(expandBox(b, 4)).map((p) => s(rotate(p, anchorEl.type === "line" ? 0 : anchorEl.rot, c)));
        return <polygon className="anchor-hint" points={poly(pts)} />;
      })()}
      {ov.eraser && <circle className="eraser-cursor" cx={s(ov.eraser.p).x} cy={s(ov.eraser.p).y} r={ov.eraser.r * cam.z} />}
      {ov.hoverPen && <circle className="pen-hover" cx={s(ov.hoverPen.p).x} cy={s(ov.hoverPen.p).y} r={Math.max(2, ov.hoverPen.r * cam.z)} style={{ fill: ov.hoverPen.color }} />}
      {ov.laser && ov.laser.pts.length > 1 && (
        <>
          <polyline className="laser-glow" points={poly(ov.laser.pts.map((q) => s(q.p)))} />
          <polyline className="laser-core" points={poly(ov.laser.pts.map((q) => s(q.p)))} />
        </>
      )}
    </svg>
  );
}

// ---------- canvas ----------

export function Canvas({ children }: { children?: React.ReactNode }) {
  const ed = useEditor();
  const viewportRef = useRef<HTMLDivElement>(null);
  const worldRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const grainRef = useRef<HTMLDivElement>(null);
  const liveGroupRef = useRef<SVGGElement>(null);
  const proxyRef = useRef<HTMLTextAreaElement>(null);
  const tool = useUi((ui) => ui.tool);
  const readOnly = useUi((ui) => ui.readOnly);
  const dark = useUi((_, e) => !!PAPERS[e.bg.paper]?.dark);
  const pages = usePages();
  const paged = pages.length > 0;
  const clip = useMemo(() => (paged ? pagesClip(pages) : ""), [paged, pages]);

  useEffect(() => {
    const vp = viewportRef.current!;
    const world = worldRef.current!;
    const bgNode = bgRef.current!;
    const grain = grainRef.current!;
    const live = liveGroupRef.current!;
    ed.viewport = vp;
    ed.camera.insets = { left: 70, right: 16, top: 64, bottom: 70 };
    const input = new InputController(ed, vp);
    ed.focusProxy = () => {
      const proxy = proxyRef.current;
      if (!proxy) return;
      // keys typed before the text box takes the focus land here, and the box picks them up
      proxy.value = "";
      proxy.focus({ preventScroll: true });
    };
    let lastZ = ed.camera.z;
    let zoomTimer = 0;
    let lastBg: BoardBackground | null = null;
    const apply = () => {
      const { x, y, z } = ed.camera;
      world.style.transform = `translate(${x}px, ${y}px) scale(${z})`;
      live.setAttribute("transform", `matrix(${z} 0 0 ${z} ${x} ${y})`);
      const paged = ed.isPaged();
      if (paged) applyDesk(bgNode, ed.bg);
      else applyBackground(bgNode, ed.bg, x, y, z);
      if (paged && ed.bg !== lastBg) applyPageVars(world, ed.bg, z);
      lastBg = paged ? ed.bg : null;
      grain.style.backgroundPosition = `${x % 512}px ${y % 512}px`;
      if (z !== lastZ) {
        // The world is its own GPU layer, so panning and pinching only move a bitmap. Once a zoom settles,
        // drop the layer for a frame so the browser redraws ink and text crisply at the new scale.
        lastZ = z;
        window.clearTimeout(zoomTimer);
        zoomTimer = window.setTimeout(() => {
          if (ed.isPaged()) applyPageVars(world, ed.bg, ed.camera.z);
          world.classList.add("rerender");
          requestAnimationFrame(() => requestAnimationFrame(() => world.classList.remove("rerender")));
        }, 180);
      }
    };
    apply();
    const offCam = ed.camera.subscribe(apply);
    const offUi = ed.subscribeUi(apply);
    // a board becomes paged when its first page arrives (e.g. from the server, after the first paint)
    const offDoc = ed.doc.subscribe((c) => {
      if (c.structural && ed.isPaged() !== (lastBg !== null)) apply();
    });
    const onPan = (e: Event) => ed.camera.panBy(0, (e as CustomEvent).detail.dy);
    window.addEventListener("sepia:pan", onPan);
    return () => {
      input.destroy();
      offCam();
      offUi();
      offDoc();
      window.removeEventListener("sepia:pan", onPan);
      window.clearTimeout(zoomTimer);
      ed.viewport = null;
      ed.focusProxy = null;
    };
  }, [ed]);

  return (
    <div className={`viewport tool-${tool}${readOnly ? " read-only" : ""}${dark ? " dark-paper" : ""}${paged ? " paged" : ""}`} ref={viewportRef}>
      <div className="paper-bg" ref={bgRef} />
      <div className="paper-grain" ref={grainRef} />
      <div className="world" ref={worldRef}>
        {paged ? (
          <>
            <PageSheets pages={pages} />
            <div className="page-clip" style={{ clipPath: `path("${clip}")` }}>
              <Scene />
            </div>
          </>
        ) : (
          <Scene />
        )}
      </div>
      <svg className="live-layer">
        {paged && (
          <defs>
            <clipPath id="sepia-live-clip">
              <path d={clip} />
            </clipPath>
          </defs>
        )}
        <g ref={liveGroupRef}>
          <path
            ref={(n) => {
              ed.livePath = n;
            }}
            clipPath={paged ? "url(#sepia-live-clip)" : undefined}
          />
        </g>
      </svg>
      <SelectionLayer />
      <OverlayLayer />
      {children}
      <textarea ref={proxyRef} className="focus-proxy" aria-hidden="true" tabIndex={-1} defaultValue="" />
    </div>
  );
}
